/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateReportCommand } from '../models/create-report-command';
import { GetAllReportsQuery } from '../models/get-all-reports-query';
import { ReportConfigurationModel } from '../models/report-configuration-model';
import { ReportModel } from '../models/report-model';
import { StartReportFlowModel } from '../models/start-report-flow-model';

@Injectable({
  providedIn: 'root',
})
export class ReportsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getAllReports
   */
  static readonly GetAllReportsPath = '/api/reports';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllReports()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllReports$Response(params?: {
    query?: GetAllReportsQuery;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<ReportModel>>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.GetAllReportsPath, 'get');
    if (params) {
      rb.query('query', params.query, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ReportModel>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getAllReports$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllReports(params?: {
    query?: GetAllReportsQuery;
    context?: HttpContext
  }
): Observable<Array<ReportModel>> {

    return this.getAllReports$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ReportModel>>) => r.body as Array<ReportModel>)
    );
  }

  /**
   * Path part for operation createReport
   */
  static readonly CreateReportPath = '/api/reports';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createReport()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createReport$Response(params?: {
    context?: HttpContext
    body?: CreateReportCommand
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.CreateReportPath, 'post');
    if (params) {
      rb.body(params.body, 'application/*+json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createReport$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  createReport(params?: {
    context?: HttpContext
    body?: CreateReportCommand
  }
): Observable<void> {

    return this.createReport$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getReportConfiguration
   */
  static readonly GetReportConfigurationPath = '/api/reports/configuration';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getReportConfiguration()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReportConfiguration$Response(params?: {
    reportTypeId?: number;
    vehicleId?: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ReportConfigurationModel>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.GetReportConfigurationPath, 'get');
    if (params) {
      rb.query('reportTypeId', params.reportTypeId, {});
      rb.query('vehicleId', params.vehicleId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ReportConfigurationModel>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getReportConfiguration$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReportConfiguration(params?: {
    reportTypeId?: number;
    vehicleId?: number;
    context?: HttpContext
  }
): Observable<ReportConfigurationModel> {

    return this.getReportConfiguration$Response(params).pipe(
      map((r: StrictHttpResponse<ReportConfigurationModel>) => r.body as ReportConfigurationModel)
    );
  }

  /**
   * Path part for operation getReport
   */
  static readonly GetReportPath = '/api/reports/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getReport()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReport$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ReportModel>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.GetReportPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ReportModel>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getReport$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getReport(params: {
    id: number;
    context?: HttpContext
  }
): Observable<ReportModel> {

    return this.getReport$Response(params).pipe(
      map((r: StrictHttpResponse<ReportModel>) => r.body as ReportModel)
    );
  }

  /**
   * Path part for operation startOperationFlow
   */
  static readonly StartOperationFlowPath = '/api/reports/start';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `startOperationFlow$Plain()` instead.
   *
   * This method doesn't expect any request body.
   */
  startOperationFlow$Plain$Response(params?: {
    context?: HttpContext
  }
): Observable<StrictHttpResponse<StartReportFlowModel>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.StartOperationFlowPath, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/plain',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<StartReportFlowModel>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `startOperationFlow$Plain$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  startOperationFlow$Plain(params?: {
    context?: HttpContext
  }
): Observable<StartReportFlowModel> {

    return this.startOperationFlow$Plain$Response(params).pipe(
      map((r: StrictHttpResponse<StartReportFlowModel>) => r.body as StartReportFlowModel)
    );
  }

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `startOperationFlow()` instead.
   *
   * This method doesn't expect any request body.
   */
  startOperationFlow$Response(params?: {
    context?: HttpContext
  }
): Observable<StrictHttpResponse<StartReportFlowModel>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.StartOperationFlowPath, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'text/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<StartReportFlowModel>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `startOperationFlow$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  startOperationFlow(params?: {
    context?: HttpContext
  }
): Observable<StartReportFlowModel> {

    return this.startOperationFlow$Response(params).pipe(
      map((r: StrictHttpResponse<StartReportFlowModel>) => r.body as StartReportFlowModel)
    );
  }

}
